"use client";

import { useContext, useSyncExternalStore } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { ScrollHeaderRevealContext } from "@/components/motion/scroll-reanimate-header";

type Props = {
  children: ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
};

const VISIBLE_CLIP = "inset(0 0% 0 0)";
const HIDDEN_CLIP = "inset(0 100% 0 0)";
const subscribeToHydration = () => () => {};
const getHydratedSnapshot = () => true;
const getServerSnapshot = () => false;
const CLIP_STYLE = {
  display: "inline-block",
  willChange: "clip-path",
  // Pad the clip area so descenders (g, j, p, q, y) aren't shaved off by the
  // inline-block boundary, while keeping layout the same.
  paddingBottom: "0.18em",
  marginBottom: "-0.18em",
};

/**
 * Cinematic text reveal via clip-path. The server and first client render keep
 * text visible for no-JS crawlers; hydrated scroll headers then hide and replay
 * the clip when they enter the viewport while scrolling down.
 */
export function TextRevealClip({
  children,
  delay = 0,
  duration = 0.7,
  className = "",
}: Props) {
  const reduce = useReducedMotion();
  const scrollHeaderReveal = useContext(ScrollHeaderRevealContext);
  const isHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydratedSnapshot,
    getServerSnapshot,
  );

  if (reduce) {
    return (
      <span className={className} style={CLIP_STYLE}>
        {children}
      </span>
    );
  }

  if (!isHydrated) {
    return (
      <span className={className} style={CLIP_STYLE}>
        {children}
      </span>
    );
  }

  if (scrollHeaderReveal) {
    const shouldHide =
      scrollHeaderReveal.isHydrated && !scrollHeaderReveal.revealed;

    return (
      <motion.span
        style={CLIP_STYLE}
        initial={false}
        animate={{ clipPath: shouldHide ? HIDDEN_CLIP : VISIBLE_CLIP }}
        transition={
          shouldHide
            ? { duration: 0 }
            : { duration, delay, ease: [0.65, 0, 0.35, 1] }
        }
        className={className}
      >
        {children}
      </motion.span>
    );
  }

  return (
    <motion.span
      style={CLIP_STYLE}
      initial={{ clipPath: HIDDEN_CLIP }}
      animate={{ clipPath: VISIBLE_CLIP }}
      transition={{ duration, delay, ease: [0.65, 0, 0.35, 1] }}
      className={className}
    >
      {children}
    </motion.span>
  );
}
